/**
 * Conditional fields: a field with `showIfFieldId` is only shown and needed
 * when that CHECKBOX field is ticked (value "true"). Fields without a
 * condition — every field created before this feature — are always active.
 *
 * Mirror of src/modules/signing/field-conditions.ts (server) — keep the two in sync.
 */
interface ConditionalField {
  id: string;
  showIfFieldId?: string | null;
}

/** Whether `field` is active given the current values (a chain of conditions is followed). */
export function isFieldActive<F extends ConditionalField>(
  field: F,
  byId: Map<string, F>,
  valueOf: (fieldId: string) => string | null | undefined,
): boolean {
  let current: F | undefined = field;
  for (let depth = 0; current?.showIfFieldId && depth < 5; depth++) {
    const box = byId.get(current.showIfFieldId);
    // The checkbox no longer exists: keep the field (safer to ask than to skip).
    if (!box) return true;
    if (valueOf(box.id) !== 'true') return false;
    current = box;
  }
  return true;
}

/** Ids of the fields that are active given the current values. */
export function activeFieldIds<F extends ConditionalField>(
  fields: F[],
  valueOf: (fieldId: string) => string | null | undefined,
): Set<string> {
  const byId = new Map(fields.map((f) => [f.id, f]));
  return new Set(fields.filter((f) => isFieldActive(f, byId, valueOf)).map((f) => f.id));
}
